import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued';

// Shared text diff viewer — wraps react-diff-viewer-continued with our design
// tokens (matches the greenColor / redColor / purpleColor palette used across
// the app). Word-level diffing gives a diffchecker.com-style view so users no
// longer need to copy-paste large text fields into an external service to see
// exactly what changed.
//
// Two layouts:
//   splitView=true  (default) — side-by-side, best when the container is wide
//                                (e.g. the parser-import full-diff modal).
//   splitView=false           — unified/inline, best when the container is
//                                narrow (e.g. the AdvertDiffModal side panel).
//
// Non-string values (numbers, booleans, arrays, objects) are coerced to a
// stable string representation so the differ never crashes on a null / number.

const toDiffString = v => {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) return v.map(toDiffString).join('\n');
  if (typeof v === 'object') {
    try {
      return JSON.stringify(v, null, 2);
    } catch {
      return String(v);
    }
  }
  return String(v);
};

// Palette tuned to the app's greenColor / redColor tokens (see tailwind.config).
// We keep line + word backgrounds distinct so word-level highlights pop even
// on the light row background.
const diffStyles = {
  variables: {
    light: {
      diffViewerBackground: '#ffffff',
      diffViewerColor: '#100C09',
      addedBackground: '#f2fadf', // greenColor/15
      addedColor: '#100C09',
      removedBackground: '#fde6e8', // redColor/15
      removedColor: '#100C09',
      wordAddedBackground: '#c9e58a', // greenColor/50
      wordRemovedBackground: '#f6b0b6', // redColor/50
      addedGutterBackground: '#dff0b8',
      removedGutterBackground: '#f9c9cd',
      gutterBackground: '#fcfcfc',
      gutterBackgroundDark: '#f5f5f5',
      highlightBackground: '#fff2ab',
      highlightGutterBackground: '#fabd23',
      codeFoldGutterBackground: '#f5f5f5',
      codeFoldBackground: '#fcfcfc',
      emptyLineBackground: '#fcfcfc',
      gutterColor: '#424242',
      addedGutterColor: '#100C09',
      removedGutterColor: '#100C09',
      codeFoldContentColor: '#424242',
      diffViewerTitleBackground: '#f5f5f5',
      diffViewerTitleColor: '#100C09',
      diffViewerTitleBorderColor: '#e5e7eb',
    },
  },
  contentText: {
    fontSize: '12px',
    lineHeight: '1.5',
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    wordBreak: 'break-word',
  },
  gutter: {
    minWidth: '28px',
    padding: '0 6px',
    fontSize: '10px',
  },
  line: {
    padding: '2px 0',
  },
  titleBlock: {
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    padding: '6px 10px',
  },
};

export const TextDiffView = ({
  oldValue,
  newValue,
  splitView = true,
  leftTitle,
  rightTitle,
  showDiffOnly = false,
  extraLinesSurroundingDiff = 2,
  compareMethod = DiffMethod.WORDS,
  className = '',
  testId,
}) => {
  const oldStr = toDiffString(oldValue);
  const newStr = toDiffString(newValue);

  // If both sides are empty there is literally nothing to diff — render a
  // neutral placeholder rather than an empty grey box that looks broken.
  if (!oldStr && !newStr) {
    return (
      <div
        data-testid={testId}
        className={
          'text-xs italic text-darkGrayColor px-3 py-4 border border-gray-200 rounded bg-lightestGrayColor ' +
          className
        }
      >
        No text to diff.
      </div>
    );
  }

  return (
    <div
      data-testid={testId}
      className={
        'text-diff-view overflow-hidden rounded border border-gray-200 bg-white ' +
        className
      }
    >
      <ReactDiffViewer
        oldValue={oldStr}
        newValue={newStr}
        splitView={splitView}
        compareMethod={compareMethod}
        useDarkTheme={false}
        leftTitle={leftTitle}
        rightTitle={rightTitle}
        hideLineNumbers={false}
        showDiffOnly={showDiffOnly}
        extraLinesSurroundingDiff={extraLinesSurroundingDiff}
        styles={diffStyles}
      />
    </div>
  );
};

export default TextDiffView;
